import { createProjectBundle } from '@jsi/scene-data/bundle';

import packageInfo from '../package.json';
import type { Events } from './events';
import type { SceneExportOptions } from './file-handler';
import { BrowserFileSystem } from './io';
import { readScenePackage, writeScenePackage, type SceneLayerValues } from './jsi-scene-package';

/** Capture image dependencies without exposing network handling to the core. */
const captureImages = async (layers: SceneLayerValues): Promise<SceneLayerValues> => {
    const captured = structuredClone(layers);
    const walk = async (value: unknown): Promise<void> => {
        if (!value || typeof value !== 'object') return;
        const fields = value as Record<string, unknown>;
        for (const [key, field] of Object.entries(fields)) {
            if ((/url$/i.test(key) || key === 'src') && typeof field === 'string' && field && !field.startsWith('data:')) {
                const url = new URL(field, location.href);
                if (!['http:', 'https:', 'blob:'].includes(url.protocol)) throw new Error(`Unsupported bundle image URL: ${key}`);
                const response = await fetch(url);
                if (!response.ok || Number(response.headers.get('content-length')) > 8000000) throw new Error(`Cannot capture bundle image: ${key}`);
                const blob = await response.blob();
                if (!blob.type.startsWith('image/') || blob.size > 8000000) throw new Error(`Bundle images must be images smaller than 8 MB: ${key}`);
                const bytes = new Uint8Array(await blob.arrayBuffer());
                let binary = '';
                for (let i = 0; i < bytes.length; i += 4096) binary += String.fromCharCode(...bytes.subarray(i, i + 4096));
                fields[key] = `data:${blob.type};base64,${btoa(binary)}`;
            } else await walk(field);
        }
    };
    await walk(captured);
    return captured;
};

export const registerJsiBundleEvents = (events: Events) => {
    events.function('doc.exportJsi', async () => {
        const options = await events.invoke('show.exportPopup', 'jsi', [(events.invoke('doc.name') ?? 'scene').replace(/\.(ssproj|jsi)$/i, '')], true) as SceneExportOptions | null;
        if (!options) return false;
        const format = options.bundleConvert ? options.bundleFormat : 'ply';
        const degree = options.serializeSettings.maxSHBands;
        if (!['ply', 'sog'].includes(format) || !Number.isInteger(degree) || degree < 0 || degree > 3) throw new Error('Invalid JSI bundle quality settings.');
        events.fire('startSpinner');
        try {
            if (!events.invoke('scene.allSplats').length) throw new Error('Load at least one splat layer to export a project.');
            const nativeProject: Uint8Array = await events.invoke('doc.serialize', {
                format: options.bundleConvert ? format : undefined,
                maxSHBands: options.bundleReduceSH ? degree : undefined,
                iterations: options.sogIterations ?? 10
            });
            const current = await readScenePackage(await events.invoke('docSerialize.jsiSceneData'));
            const layers = await captureImages(current.layers);
            const sceneData = await writeScenePackage(layers, current.manifest.scene.id, [], current.manifest);
            const bundle = await createProjectBundle({
                sceneData,
                editorProject: nativeProject,
                producer: { application: 'jsi-editor', version: packageInfo['jsi-editor-version'] }
            });
            const filename = `${options.filename.replace(/\.jsi$/i, '') || 'scene'}.jsi`;
            const writer = new BrowserFileSystem(filename).createWriter(filename);
            await writer.write(bundle);
            await writer.close();
            return true;
        } catch (error) {
            await events.invoke('showPopup', { type: 'error', header: 'JSI bundle export failed', message: String(error.message ?? error) });
            return false;
        } finally {
            events.fire('stopSpinner');
        }
    });
};
